// @ts-strict-ignore
import type {HoveredItem} from 'common/charts/contexts/segment-filter-controller';
import {LegendOverride, LegendOverrides} from 'common/types/reports/bookmark';
import {MIXPANEL_DEFAULT_THEME_ID} from 'common/query-builder/chart-customization/colors/constants';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {
  createTheme,
  deleteTheme,
  Theme,
  themesClient,
  ThemesRequestType,
  ThemesResultsMapType,
  updateTheme,
} from 'common/report/queries/app-api/themes';
import {ApiResource} from 'common/util/model-resource/api-resource';
import {StorageManager} from 'common/contexts/storage-manager/types';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {prefersDarkColorScheme} from 'common/util/dom';
import type {UrlManager} from 'common/contexts/url-manager/types';
import {ColorSchemeSetting, PersistedColorSchemeState} from 'common/report/model-resources/themes/types';

const CREATE_KEY = `create`;
export const DARK_MODE_KEY = `darkModeOn`;
export const DARK_MODE_CLASS = `dark-mode`;

export class ThemesResource extends ApiResource<Theme> {
  declare currentProject: CurrentProjectResource;
  declare localStorageManager: StorageManager;
  declare urlManager: UrlManager;
  declare globalHoveredItem: HoveredItem;
  declare _legendOverrides: LegendOverrides;
  apiClient = themesClient;

  /** Holds a map of segments to colors for rendering charts.
   * This is used in the context of a board where comparing like-to-like
   * segments is important for consuming and quickly scan data.
   */
  declare colorMappings: Map<string, {color: string; sourceId: string}>;

  constructor({
    currentProject,
    localStorageManager,
    urlManager,
  }: {
    currentProject: CurrentProjectResource;
    localStorageManager: StorageManager;
    urlManager: UrlManager;
  }) {
    super();
    this.currentProject = currentProject;
    this.localStorageManager = localStorageManager;
    this.urlManager = urlManager;
    this.globalHoveredItem = null;
    this._legendOverrides = {};
    this.colorMappings = new Map();
  }

  private async requestWrapper(loadingKey: string, callback: () => Promise<ThemesResultsMapType>) {
    this.fetchesInProgress.set(loadingKey, undefined);
    this.notifySubscribers();
    try {
      const results = await callback();

      this.update(results, {partialUpdate: false});

      return results;
    } finally {
      this.fetchesInProgress.delete(loadingKey);
      this.notifySubscribers();
    }
  }

  /** when a user retrieves a list of themes, we only want to present those they have access to */
  getAllValues() {
    return super.getAllValues().filter((theme) => theme.can_view);
  }

  patch(id: number, theme: Partial<Theme>): Promise<ThemesResultsMapType> {
    const themeId = id.toString();
    return this.requestWrapper(themeId, () => updateTheme({themeId, theme}));
  }

  create(theme: ThemesRequestType): Promise<ThemesResultsMapType> {
    return this.requestWrapper(CREATE_KEY, () => createTheme(theme));
  }

  async delete(id: number): Promise<ThemesResultsMapType> {
    const themeId = id.toString();
    const results = await deleteTheme({themeId});
    this.models.delete(themeId);
    this.apiClient.purgeCache();
    this.notifySubscribers();
    return results;
  }

  isLoading(id?: number) {
    if (id) {
      return this.fetchesInProgress.has(id.toString());
    }
    return this.fetchesInProgress.has(CREATE_KEY);
  }

  getDefaultProjectThemeId() {
    const themeId = this.currentProject.get().value?.defaultThemeId;
    if (!themeId) {
      return MIXPANEL_DEFAULT_THEME_ID;
    }

    return themeId;
  }

  getUserId(): number | undefined {
    return userCookieUtils.getUserIdentityFromCookie()?.id;
  }

  getColorSchemeStorage(): UserLocalStorageHelper<PersistedColorSchemeState> {
    return new UserLocalStorageHelper({
      context: this.localStorageManager,
      namespace: LocalStorageKey.DarkMode,
    });
  }

  getPersistedColorSchemeState(): PersistedColorSchemeState | null {
    return this.getColorSchemeStorage().getItem({
      key: DARK_MODE_KEY,
      userId: this.getUserId(),
    });
  }

  getColorSchemeSetting(): ColorSchemeSetting {
    const persistedColorSchemeState = this.getPersistedColorSchemeState();
    if (!persistedColorSchemeState) {
      return ColorSchemeSetting.Auto;
    } else if (persistedColorSchemeState.darkMode) {
      return ColorSchemeSetting.DarkMode;
    }

    return ColorSchemeSetting.LightMode;
  }

  isDarkMode(): boolean {
    return window.document.documentElement.classList.contains(`dark-mode`);
  }

  initializeColorScheme(): void {
    let darkMode: boolean;
    if (this.urlManager.getQueryStringParam(`dark-mode`)) {
      // make URL param override all settings
      darkMode = true;
    } else {
      const colorSchemeSettings = this.getColorSchemeSetting();
      if (colorSchemeSettings === ColorSchemeSetting.Auto) {
        // default to OS provided color scheme
        darkMode = prefersDarkColorScheme();
      } else {
        darkMode = this.getColorSchemeSetting() === ColorSchemeSetting.DarkMode;
      }
    }

    this.updateColorSchemeClass({darkMode});
  }

  updateColorSchemeSetting(colorSchemeSetting: ColorSchemeSetting): void {
    if (colorSchemeSetting === ColorSchemeSetting.Auto) {
      this.getColorSchemeStorage().removeItem({key: DARK_MODE_KEY, userId: this.getUserId()});
      this.updateColorSchemeClass({darkMode: prefersDarkColorScheme()});
    } else {
      const darkMode = colorSchemeSetting === ColorSchemeSetting.DarkMode;
      this.getColorSchemeStorage().setItem({
        key: DARK_MODE_KEY,
        userId: this.getUserId(),
        value: {darkMode},
      });
      this.updateColorSchemeClass({darkMode});
    }
  }

  updateColorSchemeClass({darkMode}: {darkMode: boolean}): void {
    if (darkMode) {
      window.document.documentElement.classList.add(DARK_MODE_CLASS);
    } else {
      window.document.documentElement.classList.remove(DARK_MODE_CLASS);
    }
    this.notifySubscribers();
  }

  setGlobalHoveredItem(item: HoveredItem | null) {
    this.globalHoveredItem = item;
    this.notifySubscribers();
  }

  set legendOverrides(overrides: LegendOverrides) {
    this._legendOverrides = overrides;
    this.notifySubscribers();
  }

  get legendOverrides() {
    return this._legendOverrides;
  }

  setLegendOverride(key: string, value: LegendOverride | null) {
    if (value === null) {
      delete this._legendOverrides[key];
    } else {
      this._legendOverrides[key] = value;
    }
    this.notifySubscribers();
  }

  setColorMapping(path: string, color: string, sourceId: string) {
    this.colorMappings.set(path, {color, sourceId});
  }

  clearSourceColorMappings(sourceId: string) {
    const keys = Array.from(this.colorMappings.keys());
    for (const key of keys) {
      if (this.colorMappings.get(key).sourceId === sourceId) {
        this.colorMappings.delete(key);
      }
    }
  }

  resetColorMappings() {
    this.colorMappings = new Map();
  }
}
